import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusCircleIcon, TrashIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
const mockRFPSuppliers = [{
  id: 'SUP-001',
  name: 'TechCorp Solutions',
  quotedAmount: 45000,
  totalScore: 87.5
}, {
  id: 'SUP-002',
  name: 'Global Logistics Inc',
  quotedAmount: 52000,
  totalScore: 80.0
}, {
  id: 'SUP-003',
  name: 'BuildIt Construction',
  quotedAmount: 48500,
  totalScore: 82.5
}];
interface NegotiationRecord {
  supplierId: string;
  supplierName: string;
  originalAmount: number;
  negotiatedAmount: string;
  remarks: string;
}
export function NegotiationAction() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState('In Progress');
  const [negotiations, setNegotiations] = useState<NegotiationRecord[]>([]);
  const [addNegotiationSupplier, setAddNegotiationSupplier] = useState('');
  const isReadOnly = status === 'Completed';
  const availableForNegotiation = mockRFPSuppliers.filter(s => !negotiations.some(n => n.supplierId === s.id));
  const handleAddNegotiation = () => {
    if (!addNegotiationSupplier) return;
    const supplier = mockRFPSuppliers.find(s => s.id === addNegotiationSupplier);
    if (!supplier) return;
    setNegotiations(prev => [...prev, {
      supplierId: supplier.id,
      supplierName: supplier.name,
      originalAmount: supplier.quotedAmount,
      negotiatedAmount: '',
      remarks: ''
    }]);
    setAddNegotiationSupplier('');
  };
  const handleRemoveNegotiation = (supplierId: string) => {
    setNegotiations(prev => prev.filter(n => n.supplierId !== supplierId));
  };
  const handleNegotiationChange = (supplierId: string, field: 'negotiatedAmount' | 'remarks', value: string) => {
    setNegotiations(prev => prev.map(n => n.supplierId === supplierId ? {
      ...n,
      [field]: value
    } : n));
  };
  const handleComplete = () => {
    setStatus('Completed');
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-1268">
      <div className="flex items-center justify-between" data-id="element-1269">
        <div className="flex items-center space-x-4" data-id="element-1270">
          <button onClick={() => navigate('/negotiation')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1271">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1272" />
          </button>
          <div data-id="element-1273">
            <div className="flex items-center space-x-3" data-id="element-1274">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1275">
                Process Negotiation
              </h1>
              <StatusBadge status={status} data-id="element-1276" />
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1277">
              Negotiation {id} for PR-2023-001 - Laptop Pro X
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1278">
          {!isReadOnly && <>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1279">
                <SaveIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-1280" />
                Save Draft
              </button>
              <button onClick={handleComplete} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1281">
                <CheckCircleIcon className="h-4 w-4 mr-2" data-id="element-1282" />
                Complete Negotiation
              </button>
            </>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-1283">
        <div className="mb-6" data-id="element-1284">
          <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1285">
            Supplier Negotiations
          </h3>

          {!isReadOnly && availableForNegotiation.length > 0 && <div className="flex gap-2 mb-6" data-id="element-1286">
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 bg-white" value={addNegotiationSupplier} onChange={e => setAddNegotiationSupplier(e.target.value)} data-id="element-1287">
                <option value="" data-id="element-1288">
                  Select a supplier to add negotiation record...
                </option>
                {availableForNegotiation.map(s => <option key={s.id} value={s.id} data-id="element-1289">
                    {s.name} — Quoted: ${s.quotedAmount.toLocaleString()}{' '}
                    (Score: {s.totalScore})
                  </option>)}
              </select>
              <button type="button" onClick={handleAddNegotiation} disabled={!addNegotiationSupplier} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap" data-id="element-1290">
                <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1291" />
                Add
              </button>
            </div>}

          {negotiations.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-1292">
              <p className="text-gray-500" data-id="element-1293">No negotiation records added yet.</p>
            </div> : <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1294">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1295">
                <thead className="bg-gray-50" data-id="element-1296">
                  <tr data-id="element-1297">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1298">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1299">
                      Original Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1300">
                      Negotiated Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1301">
                      Savings
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1302">
                      Remarks
                    </th>
                    {!isReadOnly && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16" data-id="element-1303"></th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1304">
                  {negotiations.map(neg => {
                const savings = neg.negotiatedAmount ? neg.originalAmount - Number(neg.negotiatedAmount) : 0;
                const savingsPercent = neg.negotiatedAmount ? (savings / neg.originalAmount * 100).toFixed(1) : null;
                return <tr key={neg.supplierId} data-id="element-1305">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900" data-id="element-1306">
                          {neg.supplierName}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1307">
                          ${neg.originalAmount.toLocaleString()}
                        </td>
                        <td className="px-4 py-4" data-id="element-1308">
                          <input type="number" className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 disabled:bg-gray-100" value={neg.negotiatedAmount} onChange={e => handleNegotiationChange(neg.supplierId, 'negotiatedAmount', e.target.value)} placeholder="Amount" disabled={isReadOnly} data-id="element-1309" />
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-1310">
                          {savingsPercent ? <span className={savings >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'} data-id="element-1311">
                              {savings >= 0 ? '-' : '+'}$
                              {Math.abs(savings).toLocaleString()} (
                              {savingsPercent}%)
                            </span> : <span className="text-gray-400" data-id="element-1312">—</span>}
                        </td>
                        <td className="px-4 py-4" data-id="element-1313">
                          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 disabled:bg-gray-100" value={neg.remarks} onChange={e => handleNegotiationChange(neg.supplierId, 'remarks', e.target.value)} placeholder="Notes..." disabled={isReadOnly} data-id="element-1314" />
                        </td>
                        {!isReadOnly && <td className="px-4 py-4 text-right" data-id="element-1315">
                            <button type="button" onClick={() => handleRemoveNegotiation(neg.supplierId)} className="text-red-400 hover:text-red-600 transition-colors p-1" data-id="element-1316">
                              <TrashIcon className="h-5 w-5" data-id="element-1317" />
                            </button>
                          </td>}
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}